import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faThumbsDown, faThumbsUp } from '@fortawesome/free-solid-svg-icons';

import { useSurveySubmit } from '../../hooks/survey';
import type {
  FeedbackPageType,
  FeedbackTrigger,
} from '../../hooks/survey/useProjectFeedbackPrompt';
import { captureFunnelEvent } from '../../services/telemetry';

export type FeedbackRating = 'thumbs-up' | 'thumbs-down';

export interface FeedbackPopoverProps {
  projectId: string;
  pageType: FeedbackPageType;
  trigger: FeedbackTrigger;
  onClose: () => void;
  onSubmitted: () => void;
  /** Anchor element (the toolbar button that opened this popover). */
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  /** `bottom` for the top toolbar; `top` for the mobile bottom bar. */
  placement?: 'bottom' | 'top';
}

const PRIMARY_PILL = `
  cursor-pointer rounded-full bg-gray-800 px-5 py-2 text-sm font-medium
  text-white transition-all duration-100
  hover:bg-gray-700
  active:scale-95
  disabled:cursor-not-allowed disabled:opacity-50
`;

/** Thumbs-up / thumbs-down panel with an optional comment. */
export const FeedbackPopover: React.FC<FeedbackPopoverProps> = ({
  projectId,
  pageType,
  trigger,
  onClose,
  onSubmitted,
  anchorRef,
  placement = 'bottom',
}) => {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const submittedRef = useRef(false);
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { submitImprovementSurvey, loading } = useSurveySubmit();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    const onMouseDown = (e: MouseEvent) => {
      if (!panelRef.current || panelRef.current.offsetParent === null) return;
      const target = e.target as Node | null;
      if (!target) return;
      if (panelRef.current.contains(target)) return;
      if (anchorRef.current?.contains(target)) return;
      handleClose();
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [handleClose, anchorRef]);

  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(() => {
      onSubmitted();
      onClose();
    }, 1000);
    return () => clearTimeout(timer);
  }, [submitted, onSubmitted, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rating || loading || submitted) return;

    const selectedLabel = rating === 'thumbs-up' ? 'Thumbs up' : 'Thumbs down';
    const textInput = comment.trim() || undefined;

    try {
      await submitImprovementSurvey(projectId, {
        selectedOption: rating,
        selectedLabel,
        textInput,
        dismissed: false,
      });
    } catch (error) {
      console.error('Error submitting project feedback:', error);
    } finally {
      submittedRef.current = true;
      captureFunnelEvent('feedback_prompt_submitted', {
        trigger,
        projectId,
        type: pageType,
        rating,
        text: textInput ?? null,
      });
      setSubmitted(true);
    }
  };

  const thumbClass = (value: FeedbackRating) => `
    flex h-10 w-10 cursor-pointer items-center justify-center rounded-full
    border transition-colors
    ${
      rating === value
        ? 'border-yellow-500 bg-yellow-50 text-yellow-600'
        : 'border-gray-300 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
    }
  `;

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-label="Project feedback"
      className={`
        absolute right-0 z-50 w-56 max-w-[calc(100vw-1.5rem)] rounded-2xl border
        border-gray-200 bg-white p-3 shadow-lg
        ${
          placement === 'top'
            ? 'bottom-[calc(100%+8px)]'
            : 'top-[calc(100%+8px)]'
        }
      `}
    >
      <button
        type="button"
        onClick={handleClose}
        className={`
          absolute top-2 right-2 rounded-full p-1 text-gray-400
          transition-colors
          hover:bg-gray-100 hover:text-gray-600
          focus:ring-2 focus:ring-gray-300 focus:outline-none
        `}
        aria-label="Close feedback"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
      {submitted ? (
        <p
          className={`
            py-2 pr-6 text-center text-sm font-semibold text-green-600
          `}
        >
          Thanks for the feedback
        </p>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-2">
          <p className="pr-6 text-sm font-medium text-gray-800">
            How was this result?
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              className={thumbClass('thumbs-up')}
              aria-pressed={rating === 'thumbs-up'}
              aria-label="Thumbs up"
              onClick={() => setRating('thumbs-up')}
            >
              <FontAwesomeIcon icon={faThumbsUp} className="text-lg" />
            </button>
            <button
              type="button"
              className={thumbClass('thumbs-down')}
              aria-pressed={rating === 'thumbs-down'}
              aria-label="Thumbs down"
              onClick={() => setRating('thumbs-down')}
            >
              <FontAwesomeIcon icon={faThumbsDown} className="text-lg" />
            </button>
          </div>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Optional comments"
            rows={2}
            className={`
              w-full resize-none rounded-lg border border-gray-300 p-2 text-sm
              text-gray-800
              focus:border-yellow-500 focus:ring-2 focus:ring-yellow-500
              focus:outline-none
            `}
          />
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!rating || loading}
              className={PRIMARY_PILL}
            >
              Submit
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
